// src/pages/AdminUsers.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Table,
  Button,
  Form,
  Modal,
  Row,
  Col,
  Alert,
  InputGroup,
} from 'react-bootstrap';
import http from '../services/http';
import { getUser } from '../services/auth';

// Mapea el rol técnico a algo entendible en UI
const roleToUi = (r) => (r === 'admin2' ? 'view' : r);
const uiToRole = (r) => (r === 'view' ? 'admin2' : r);

export default function AdminUsers() {
  const me = getUser(); // {id, username, role}
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [err, setErr] = useState('');

  // modal edición
  const [showEdit, setShowEdit] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editData, setEditData] = useState({
    username: '',
    role: 'view',
    enabled: true,
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  // modal borrar
  const [showDel, setShowDel] = useState(false);
  const [delRow, setDelRow] = useState(null);

  const load = async () => {
    setErr('');
    try {
      const { data } = await http.get('/users');
      // normalizo roles para mostrar "view"
      setUsers((data || []).map(u => ({ ...u, role: roleToUi(u.role) })));
    } catch (e) {
      console.error(e);
      setErr('No se pudieron cargar los usuarios.');
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return users;
    const q = query.toLowerCase();
    return users.filter(u =>
      [u.username, u.role, u.enabled ? 'habilitado' : 'deshabilitado']
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [users, query]);

  // ====== Editar ======
  const openEdit = (row) => {
    setEditRow(row);
    setEditData({
      username: row.username,
      role: row.role,           // view|admin|dev
      enabled: !!row.enabled,
      password: '',
    });
    setShowPassword(false);
    setShowEdit(true);
  };
  const closeEdit = () => { setShowEdit(false); setEditRow(null); };

  const onEditChange = (e) => {
    const { name, type, checked, value } = e.target;
    setEditData(d => ({ ...d, [name]: type === 'checkbox' ? checked : value }));
  };

  const saveEdit = async () => {
    if (!editRow) return;
    setErr('');
    try {
      const payload = {
        role: uiToRole(editData.role),
        enabled: editData.enabled ? 1 : 0,
      };
      if (editData.password.trim()) payload.password = editData.password.trim();

      await http.put(`/users/${editRow.id}`, payload);
      closeEdit();
      await load();
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.error || 'Error actualizando usuario');
    }
  };

  // ====== Borrar ======
  const openDel = (row) => { setDelRow(row); setShowDel(true); };
  const closeDel = () => { setShowDel(false); setDelRow(null); };

  const del = async () => {
    if (!delRow) return;
    setErr('');
    try {
      await http.delete(`/users/${delRow.id}`);
      closeDel();
      await load();
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.error || 'No se pudo eliminar');
    }
  };

  // reglas de permiso (front):
  const canEdit = (row) => {
    if (me?.role === 'dev') return true;               // dev puede todo
    if (row.role === 'dev') return false;              // admin no toca dev
    return true;
  };
  const canDelete = (row) => {
    if (me?.id === row.id) return false;               // no se borra a sí mismo
    return canEdit(row);
  };

  return (
    <Container className="mt-4" style={{ maxWidth: 1000 }}>
      <h2 className="mb-3">Usuarios</h2>

      <Row className="g-2 align-items-center mb-3">
        <Col md={6}>
          <Form.Control
            placeholder="Buscar por usuario / rol / estado…"
            value={query}
            onChange={(e)=>setQuery(e.target.value)}
          />
        </Col>
        <Col className="text-md-end">
          <Button
            href="/admin/users/new"
            variant="primary"
          >
            + Crear usuario
          </Button>
        </Col>
      </Row>

      {err && <Alert variant="danger">{err}</Alert>}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Usuario</th>
            <th>Rol</th>
            <th>Estado</th>
            <th>Creado</th>
            <th style={{width:120}}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((u, idx) => (
            <tr key={u.id}>
              <td>{idx + 1}</td>
              <td>{u.username}</td>
              <td>
                {u.role === 'dev' && <span className="badge bg-dark">dev</span>}
                {u.role === 'admin' && <span className="badge bg-warning text-dark">admin</span>}
                {u.role === 'view' && <span className="badge bg-secondary">view</span>}
              </td>
              <td>{u.enabled ? 'Habilitado' : 'Deshabilitado'}</td>
              <td>{u.created_at ? new Date(u.created_at).toLocaleString() : '—'}</td>
              <td className="text-center">
                <div className="d-flex gap-2 justify-content-center">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => openEdit(u)}
                    disabled={!canEdit(u)}
                    title={canEdit(u) ? 'Editar' : 'Sin permiso'}
                  >
                    Editar
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => openDel(u)}
                    disabled={!canDelete(u)}
                    title={canDelete(u) ? 'Borrar' : 'No permitido'}
                  >
                    Borrar
                  </Button>
                </div>
              </td>
            </tr>
          ))}
          {!filtered.length && (
            <tr><td colSpan={6} className="text-center text-muted py-4">Sin resultados</td></tr>
          )}
        </tbody>
      </Table>

      {/* Modal EDITAR */}
      <Modal show={showEdit} onHide={closeEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar usuario</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editRow && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Usuario</Form.Label>
                <Form.Control value={editRow.username} disabled />
              </Form.Group>

              <Row className="g-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Rol</Form.Label>
                    <Form.Select
                      name="role"
                      value={editData.role}
                      onChange={onEditChange}
                      disabled={me?.role !== 'dev' && editRow.role === 'dev'}
                    >
                      <option value="view">view</option>
                      <option value="admin">admin</option>
                      <option value="dev" disabled={me?.role !== 'dev'}>dev</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mt-3">
                <Form.Label>Cambiar contraseña (opcional)</Form.Label>
                <div className="input-password-wrapper">
                  <Form.Control
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Dejar vacío para no cambiar"
                    value={editData.password}
                    onChange={onEditChange}
                  />
                  <i
                    className={`toggle-eye bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}
                    onClick={() => setShowPassword(v => !v)}
                  ></i>
                </div>
              </Form.Group>

            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit}>Cancelar</Button>
          <Button variant="primary" onClick={saveEdit}>Guardar</Button>
        </Modal.Footer>
      </Modal>

      {/* Modal BORRAR */}
      <Modal show={showDel} onHide={closeDel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar borrado</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {delRow ? (
            <>
              ¿Eliminar al usuario <strong>{delRow.username}</strong>?
              <div className="small text-muted mt-2">
                Esta acción no se puede deshacer.
              </div>
            </>
          ) : '—'}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDel}>Cancelar</Button>
          <Button variant="danger" onClick={del}>Eliminar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}


